'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { getMicrocopyValue } from '@lib/util/microcopy';
import { Star } from 'phosphor-icons';
import { useCallback, useMemo } from 'react';
import { TreeAccordion, c } from 'ui-components';
import { getFacetTreeIdPrefix } from '../../facetTypes';
import { RATING_RANGES } from '../../utils/facetRanges';
import { FlatFacetShell } from '../FlatFacetShell';
import { useSearchControllers } from '../headless/SearchDefinitionContext';
import { NumericFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { RatingFacetProps } from './RatingFacet.types';

const getValueId = (v: NumericFlatFacetValue): string =>
  `rating-${v.start}-${v.end}`;

export const RatingFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: RatingFacetProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const ratingLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.filter-rating-label',
    'Rating'
  );

  const clearLabel = getMicrocopyValue(
    searchLabelsMicrocopy,
    'search.clear',
    'Clear'
  );

  const ratingsBundle = useMemo(
    () => microcopySetToResourceBundles(searchValuesMicrocopy ?? []).ratings,
    [searchValuesMicrocopy]
  );

  const controllers = useSearchControllers();
  const ratingFacet = controllers.useRatingFacet();
  const {
    values,
    selectedIds,
    selectedCount,
    isReady,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({ facet: ratingFacet, getValueId });

  const getLabel = useCallback(
    (start: number, end: number) => {
      const range = RATING_RANGES.find(
        (r) => r.start === start && r.end === end
      );
      if (!range) return `${start}-${end}`;
      return ratingsBundle?.[range.bundleKey] ?? range.label;
    },
    [ratingsBundle]
  );

  const isDark = variant === 'mobile';

  const treeItems = useMemo(
    () =>
      [...values]
        .sort((a, b) => b.start - a.start)
        .map((v) => ({
          id: getValueId(v),
          label: getLabel(v.start, v.end),
          count: v.numberOfResults,
          disabled:
            !isReady || (v.numberOfResults === 0 && v.state !== 'selected'),
          icon: (
            <Star
              size={16}
              weight="fill"
              className={c(
                'shrink-0',
                isDark ? 'text-grey-400' : 'text-content-muted'
              )}
            />
          ),
        })),
    [values, getLabel, isDark, isReady]
  );

  return (
    <FlatFacetShell
      title={ratingLabel}
      clearLabel={clearLabel}
      selectedCount={selectedCount}
      onClear={handleClear}
      variant={variant}
      className={className}
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={!isReady}
    >
      <TreeAccordion
        idPrefix={getFacetTreeIdPrefix(variant)}
        items={treeItems}
        selectionMode="multiple"
        selectedIds={selectedIds}
        onSelectMany={handleSelectMany}
        isDarkTheme={isDark}
        size={variant === 'sidebar' ? 'default' : 'sm'}
        reserveExpandSpace={variant !== 'horizontal'}
      />
    </FlatFacetShell>
  );
};
